import React, { useEffect, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../../app/hooks";
import { Session } from "../../features/sessions/models/Session";
import { SessionConflicts } from "../../features/sessions/models/SessionConflict";
import { checkSessionConflicts } from "../../features/sessions/slice-extensions/conflicts-slice-extension";
import { toCalendarDateISO } from "../../features/sessions/utils/dateWire";
import {
  formatDateDisplay,
  formatTimeDisplay,
} from "../../features/sessions/utils/DisplayMappers";

interface SessionConflictNoticeProps {
  session: Session;
  /** When editing, the session's own occurrences are not conflicts. */
  excludeSessionId?: string;
}

const SHOWN_CONFLICTS = 3;

/**
 * Advisory overlap warning for the session being configured: debounce the form
 * state, ask the backend which of the next occurrences collide with another
 * session, and list them. Never blocks saving.
 */
const SessionConflictNotice: React.FC<SessionConflictNoticeProps> = ({
  session,
  excludeSessionId,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [result, setResult] = useState<SessionConflicts | null>(null);

  const { dateStart, timeLocal, timeZone, duration, recurrenceType } = session;
  const recurrenceDays = session.recurrenceDays?.join(",");
  const { recurrenceDaysNumber, finishAfterOccurrences } = session;
  const recurrenceDateEnd = session.recurrenceDateEnd?.toISOString();

  useEffect(() => {
    if (!dateStart || !timeLocal || !duration) {
      setResult(null);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        const conflicts = await dispatch(
          checkSessionConflicts({
            dateStart: toCalendarDateISO(dateStart),
            timeLocal,
            timeZone,
            duration,
            recurrenceType,
            recurrenceDays: recurrenceDays ? recurrenceDays.split(",") : undefined,
            recurrenceDaysNumber,
            recurrenceDateEnd,
            finishAfterOccurrences,
            excludeBookingId: excludeSessionId || undefined,
          })
        ).unwrap();
        setResult(conflicts);
      } catch {
        // Advisory only — a failed check never bothers the instructor.
        setResult(null);
      }
    }, 600);
    return () => clearTimeout(timer);
  }, [
    dispatch,
    dateStart,
    timeLocal,
    timeZone,
    duration,
    recurrenceType,
    recurrenceDays,
    recurrenceDaysNumber,
    recurrenceDateEnd,
    finishAfterOccurrences,
    excludeSessionId,
  ]);

  if (!result || result.conflicts.length === 0) return null;

  const shown = result.conflicts.slice(0, SHOWN_CONFLICTS);
  const hiddenCount = result.conflicts.length - shown.length;

  return (
    <div className="flex items-start gap-2 rounded-lg bg-secondary-95 px-3 py-2 text-sm text-secondary-30 dark:bg-secondary-20 dark:text-secondary-80">
      <AlertTriangle size={16} strokeWidth={2} className="mt-0.5 shrink-0" />
      <div>
        <p className="font-medium">{t("sessions.conflicts.title")}</p>
        <ul className="mt-1 space-y-0.5">
          {shown.map((conflict) => (
            <li key={`${conflict.date}-${conflict.conflictingSessionId}`}>
              {t("sessions.conflicts.entry", {
                date: formatDateDisplay(conflict.date),
                title: conflict.conflictingTitle,
                time: formatTimeDisplay(conflict.conflictingTime),
              })}
            </li>
          ))}
        </ul>
        {(hiddenCount > 0 || result.hasMore) && (
          <p className="mt-1">
            {t("sessions.conflicts.more", { count: Math.max(hiddenCount, 1) })}
          </p>
        )}
      </div>
    </div>
  );
};

export default SessionConflictNotice;
